// Made-up chart data for the public landing page (no real patients).
import type { Entry } from '@/types/api';
import type { Code, Material, Surface } from '@/utils/dental';
import type { Draft } from '../dictation';

let seq = 0;

type DemoInput = {
  tooth: number;
  code: Code;
  surfaces?: Surface[];
  material?: Material | null;
  note?: string | null;
  status?: Entry['status'];
  createdAt: string;
  by?: string;
};

export function demoEntry(e: DemoInput): Entry {
  seq += 1;
  const author = { id: 'demo', name: e.by ?? 'Dr. Demo' };
  const status = e.status ?? 'APPROVED';
  return {
    id: `demo-${seq}`,
    clientId: `demo-${seq}`,
    patientId: 'demo',
    visitId: null,
    tooth: e.tooth,
    surfaces: e.surfaces ?? [],
    code: e.code,
    material: e.material ?? null,
    note: e.note ?? null,
    source: status === 'PENDING' ? 'DICTATION' : 'MANUAL',
    status,
    kind: 'EXISTING',
    transcript: null,
    transcriptId: null,
    confidence: null,
    corrected: false,
    rejectReason: null,
    createdBy: author,
    createdAt: e.createdAt,
    approvedBy: status === 'APPROVED' ? author : null,
    approvedAt: status === 'APPROVED' ? e.createdAt : null,
  };
}

/** A dictated draft shown as a pending entry. */
export function draftEntry(d: Draft, createdAt: string): Entry {
  return {
    ...demoEntry({
      tooth: d.tooth,
      code: d.code,
      surfaces: d.surfaces,
      material: d.material,
      note: d.note,
      status: 'PENDING',
      createdAt,
    }),
    transcript: d.transcript,
    confidence: d.confidence,
  };
}

/** A patient with some history: wisdom teeth out, a gold crown, two fillings. */
export const BASE_ENTRIES: Entry[] = [
  ...[18, 28, 38, 48].map((tooth) =>
    demoEntry({ tooth, code: 'missing', createdAt: '2022-04-11T09:00:00Z' })
  ),
  demoEntry({
    tooth: 47,
    code: 'crown',
    material: 'gold',
    createdAt: '2023-05-10T09:30:00Z',
    by: 'Dr. Kovács',
  }),
  demoEntry({
    tooth: 24,
    code: 'filling',
    surfaces: ['O'],
    material: 'amalgam',
    createdAt: '2022-11-20T10:00:00Z',
  }),
  demoEntry({
    tooth: 15,
    code: 'filling',
    surfaces: ['D', 'O'],
    material: 'composite',
    createdAt: '2024-02-02T08:40:00Z',
    by: 'Dr. Kovács',
  }),
];

/** The tooth map: a few teeth with a story worth tapping. */
export const MAP_ENTRIES: Entry[] = [
  ...BASE_ENTRIES,
  demoEntry({
    tooth: 16,
    code: 'caries',
    surfaces: ['M', 'O'],
    createdAt: '2024-09-03T08:10:00Z',
    by: 'Dr. Kovács',
  }),
  demoEntry({
    tooth: 16,
    code: 'filling',
    surfaces: ['M', 'O'],
    material: 'composite',
    createdAt: '2024-09-17T08:30:00Z',
    by: 'Dr. Kovács',
  }),
  demoEntry({
    tooth: 36,
    code: 'periapical',
    createdAt: '2025-01-14T10:00:00Z',
    by: 'Dr. Petrović',
  }),
  demoEntry({
    tooth: 36,
    code: 'root_canal',
    createdAt: '2025-02-04T10:15:00Z',
    by: 'Dr. Petrović',
  }),
  demoEntry({
    tooth: 36,
    code: 'crown',
    material: 'zirconia',
    createdAt: '2025-03-11T11:00:00Z',
    by: 'Dr. Petrović',
  }),
  demoEntry({
    tooth: 11,
    code: 'veneer',
    material: 'ceramic',
    createdAt: '2025-06-20T09:00:00Z',
    by: 'Dr. Hansen',
  }),
];
